<template>
  <div :style="agencyTheme">
    <div :class="[!isPlatformIs24 && $style.searchBarContainer]">
      <SearchBarWrapper
        v-if="showSearchBar"
        :allowEmptyLocation="true"
        :class="$style.searchBar"
        :searchModel="searchModel"
        :isMapViewSelected="false"
        @search="onSearch"
      />
      <ResultsSortBySelect
        v-if="showSearchBarSorting"
        data-test="mobile-searchbar-sortby"
        :class="$style.resultsSortBySelect"
        :sortingLabels="sortingLabels"
        :searchModel="searchModel"
        @onSortingChanged="sortingChanged"
      />
    </div>

    <div
      v-if="!fullSearchDataFetchError"
      :class="[$style.stickyParent, $style.resultListPage]"
      data-test="result-list-container"
    >
      <!-- Result List Header -->
      <ResultListHeader
        :isShowingMap="false"
        :resultCount="resultCount"
        :searchModel="searchModel"
        :isLoading="fullSearchIsLoading"
        :loadingText="$t('loading')"
        :isPlatformIs24="isPlatformIs24"
        :searchHeading="searchHeading"
        :isNoResultsPage="showNoResultsWarning"
        :searchHeadingWithResults="searchHeadingWithResults"
        :sortingLabels="sortingLabels"
        @onSortingChanged="sortingChanged"
      />

      <ResultList
        v-if="showFullSearchResultList"
        :searchResult="fullSearchResult"
        :gtmPageType="GTMPageType.RESULT_LIST_PAGE"
      />

      <NoResultsWarning
        v-if="showNoResultsWarning"
        :class="$style.noResults"
        :isPlatformIs24="isPlatformIs24"
        :showSaveSearchButton="false"
        :noResultsTitle="$t('resultlist.noResults.headline')"
        :noResultsDescription="$t('resultlist.noResults.description')"
        :suggestionsTitle="$t('resultlist.noResults.suggestionsTitle')"
        :suggestions="noResultsSuggestions"
      />

      <template v-if="fullSearchIsLoading">
        <HgListingCardPlaceholder
          v-for="index in 20"
          :key="index"
          :class="$style.listingCardPlaceholder"
        />
      </template>

      <HgPaginationSelector
        v-if="showPagination"
        :currentPage="fullSearchResult?.page || 1"
        :totalPages="fullSearchResult?.pageCount || 1"
        :pageRoute="pageLink"
        :class="$style.paginationHolder"
        @paginationClicked="trackPaginationClicked"
      />
    </div>

    <ErrorRetry
      v-if="fullSearchDataFetchError"
      v-bind="errorRetryProps"
      @retryAction="onSearch(searchModel)"
    />
  </div>
</template>

<!-- eslint-disable vue/no-unused-properties -->
<script lang="ts">
import SearchBarWrapper from '@/components/SearchBar/SearchBarWrapper.vue';
import { getPassThroughHeaders } from '@/helpers/RequestHeaders/RequestHeaders';
import { getSSRContext } from '@/helpers/SSR/ssr';
import { getLayoutSolutionRequest, isValidCssHexColor } from '@/resultlist/layoutSolutionRequest';
import { createLayoutSolutionSRPRoute } from '@/resultlist/layoutSolutionSrpUri';
import { isPermanentError } from '@/store/modules/result-list';
import {
  HgListingCardPlaceholder,
  HgPaginationSelector,
  HgSupportedLanguage,
} from '@smg-real-estate/domus';
import { Fieldset, SearchModel } from '@smg-real-estate/hg-search-libs';
import pWaitFor from 'p-wait-for';
import { computed, defineComponent, type PropType } from 'vue';
import { mapGetters, mapState } from 'vuex';

import { GTMPageType } from '@/helpers/GTM/GTMParameters';
import { isPlatformIs24 } from '@/helpers/platformDetection';
import { GeoLocation } from '@smg-real-estate/geo-libs';

import ResultList from '@/components/ResultList/ResultList.vue';
import {
  ErrorRetry,
  NoResultsWarning,
  ResultListHeader,
  ResultsSortBySelect,
} from '@smg-real-estate/seeker-app-shared-components';

import { useErrorRetry } from '@/composables/useErrorRetry';
import { useThemeStyle } from '@/composables/useThemeStyle';
import { getListingImages } from '@/helpers/ListingData/ListingData';
import { getPreloadLinks, PreloadLink } from '@/helpers/PreloadLinks/PreloadLinksHelper';
import {
  trackPaginationClicked,
  trackResultListVirtualPageView,
  trackSearchSubmit,
} from '@/resultlist/LayoutSolutionResultListPage.tracking';
import {
  generateAlternateLinks,
  generatePageDescription,
  generatePageTitle,
  generateSearchHeading,
} from '@/resultlist/meta';
import { LayoutSolutionRequest, ResultListHeaderSorting } from '@/types';
import { HeadSafe, SafeLink } from '@unhead/vue';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'LayoutSolutionResultListPage',
  components: {
    SearchBarWrapper,
    ResultListHeader,
    ResultList,
    NoResultsWarning,
    HgListingCardPlaceholder,
    HgPaginationSelector,
    ErrorRetry,
    ResultsSortBySelect,
  },
  provide(): { searchModel: SearchModel } {
    return {
      searchModel: this.searchModel as SearchModel,
    };
  },
  props: {
    searchModel: {
      required: true,
      type: Object as PropType<SearchModel>,
    },
  },
  setup() {
    const i18n = useI18n();
    return {
      ssrContext: getSSRContext(),
      i18n,
    };
  },
  data: () => ({
    lazyLoadDelay:
      typeof window !== 'undefined' && Object.hasOwnProperty.call(window, 'Cypress') ? 10 : 300,
    isPlatformIs24: isPlatformIs24(),
    themeStyleCleanup: null as (() => void) | null,
  }),
  head(): HeadSafe {
    const description = {
      name: 'description',
      vmid: 'description',
      content: this.pageDescription,
    };
    return {
      title: this.pageTitle,
      meta: [
        description,
        {
          name: 'robots',
          vmid: 'robots',
          content: 'noindex, nofollow',
        },
      ],
      link: [this.canonicalLink, ...this.alternateLinks, ...this.preloadLinks].filter(
        Boolean,
      ) as SafeLink[],
    };
  },
  computed: {
    showSearchBarSorting() {
      return !(
        this.isPlatformIs24 ||
        this.fullSearchResult?.resultCount === undefined ||
        this.fullSearchResult?.resultCount === 0
      );
    },
    errorRetryProps() {
      return useErrorRetry(this.i18n);
    },
    agencyTheme() {
      const { primaryColor, primaryTextColor } = this.layoutSolutionRequest || {};

      return {
        ...(isValidCssHexColor(primaryColor)
          ? {
              '--theme-color-primary': `#${primaryColor}`,
              '--theme-color-primary-hover': `#${primaryColor}`,
              '--theme-color-primary-active': `#${primaryColor}`,
            }
          : {}),
        ...(isValidCssHexColor(primaryTextColor)
          ? {
              '--theme-color-primary-text': `#${primaryTextColor}`,
              '--theme-color-text-fill': `#${primaryTextColor}`,
            }
          : {}),
      };
    },
    GTMPageType() {
      return GTMPageType;
    },
    ...mapGetters({
      fullSearchResult: 'resultList/fullSearchResult',
      fullSearchDataFetchError: 'resultList/fullSearchDataFetchError',
      fullSearchIsLoading: 'resultList/fullSearchIsLoading',
      resultSearchModel: 'resultList/lastSearchModel',
      getGeoLocationsById: 'geo/getGeoLocationsById',
    }),
    ...mapState({
      agencyConfiguration: (state: any) => state.layoutSolution.agencyConfiguration,
    }),

    hasFullSearchResult(): boolean {
      return !!this.fullSearchResult;
    },
    resultCount(): number | undefined {
      return this.fullSearchResult?.resultCount;
    },
    currentLocale(): HgSupportedLanguage {
      return this.i18n.locale.value as HgSupportedLanguage;
    },
    showFullSearchResultList(): boolean {
      return !this.fullSearchIsLoading && this.hasFullSearchResult && this.resultCount !== 0;
    },
    showNoResultsWarning(): boolean {
      return !this.fullSearchIsLoading && this.resultCount === 0;
    },
    showPagination(): boolean {
      return !this.fullSearchIsLoading && this.hasFullSearchResult && this.resultCount !== 0;
    },
    pageDescription(): string {
      return generatePageDescription(
        this.searchModel,
        this.fullSearchResult,
        this.fullSearchDataFetchError,
        this.i18n,
        this.$store,
      );
    },
    pageTitle(): string {
      return generatePageTitle(this.searchModel, this.i18n, this.$store);
    },
    canonicalLink(): { rel: string; href: string } {
      const { chooseType, locations, objectTypes, page } = this.searchModel;
      const sm = new SearchModel()
        .withChooseType(chooseType)
        .withObjectTypes(objectTypes)
        .withLocations(locations);

      const route = createLayoutSolutionSRPRoute(
        this.currentLocale,
        this.searchModel,
        this.$route,
        this.$store,
      );
      const { href } = this.$router.resolve(route);
      const pageInfo = page > 1 ? `?ep=${page}` : '';

      return {
        rel: 'canonical',
        href: `${import.meta.env.VUE_PLATFORM_BASE_URL}${href}${pageInfo}`,
      };
    },
    alternateLinks(): { rel: string; href: string; hreflang: string }[] {
      return generateAlternateLinks(this.alternatePageLink);
    },
    preloadLinks(): PreloadLink[] {
      if (this.fullSearchResult?.listings?.length) {
        const { listing, listingCard } = this.fullSearchResult.listings[0];
        return getPreloadLinks(getListingImages(listing), listingCard);
      }

      return [];
    },
    readyToRenderSSR(): boolean {
      if (this.fullSearchIsLoading) {
        return false;
      }
      if (this.hasFullSearchResult) {
        return true;
      }
      return this.fullSearchDataFetchError;
    },
    searchLocationHierarchyData() {
      const locations: GeoLocation[] = this.getGeoLocationsById(this.searchModel.locations);
      return locations.reduce((map, location) => {
        return {
          ...map,
          [location.id]: {
            parentPath: location.parentPaths ? location.parentPaths[0] : [],
            children: location.children,
          },
        };
      }, {});
    },
    layoutSolutionRequest(): LayoutSolutionRequest | undefined {
      return getLayoutSolutionRequest(this.$route.query);
    },
    showSearchBar(): boolean {
      return this.layoutSolutionRequest?.withSearchbar ?? true;
    },
    errorTitle(): string {
      return this.$t('error.search.title').toString();
    },
    errorDescription(): string {
      return this.$t('error.search.description').toString();
    },
    errorRetryText(): string {
      return this.$t('error.search.retry').toString();
    },
    sortingLabels(): Record<string, string> {
      return this.$tm('resultsList.sorting') as Record<string, string>;
    },
    noResultsSuggestions(): string[] {
      return this.$tm('resultlist.noResults.suggestions') as string[];
    },
    searchHeadingWithResults(): string {
      return `${this.resultCount} ${this.$t('resultlist.results', this.resultCount ?? 0)} - ${this.searchHeading}`;
    },
    searchHeading(): string {
      return generateSearchHeading(this.searchModel, this.i18n, this.$store, this.resultCount);
    },
  },
  watch: {
    searchModel() {
      this.doSearch();
    },
    async currentLocale() {
      await trackResultListVirtualPageView(
        this.i18n.locale.value as HgSupportedLanguage,
        this.searchModel,
        this.fullSearchResult,
      );
    },
    fullSearchResult: {
      immediate: true,
      async handler() {
        if (this.hasFullSearchResult) {
          await trackResultListVirtualPageView(
            this.i18n.locale.value as HgSupportedLanguage,
            this.searchModel,
            this.fullSearchResult,
          );
        }
      },
    },
    fullSearchIsLoading() {
      if (typeof document !== 'undefined') {
        document.title = this.pageTitle;
      }
    },
  },
  mounted() {
    if (
      (!this.hasFullSearchResult ||
        (this.resultSearchModel && !this.searchModel.isEqual(this.resultSearchModel))) &&
      !this.fullSearchDataFetchError
    ) {
      this.doSearch();
    }

    // Initialize theme style composable
    const layoutSolutionRequestRef = computed(() => this.layoutSolutionRequest);
    this.themeStyleCleanup = useThemeStyle(layoutSolutionRequestRef).cleanup;

    // Detect if we're inside an iframe and add class for conditional styling
    if (typeof window !== 'undefined' && window.self !== window.top) {
      document.body.classList.add('is-iframe');
    }
  },
  unmounted() {
    // Cleanup theme styles
    if (this.themeStyleCleanup) {
      this.themeStyleCleanup();
    }
  },
  methods: {
    trackPaginationClicked,
    onSearch(newSearchModel: SearchModel) {
      const isSearchModelChanged = !this.searchModel.isEqual(newSearchModel);
      const shouldRetryListSearch = !!this.fullSearchDataFetchError;

      if (isSearchModelChanged) {
        this.$router
          .push(
            createLayoutSolutionSRPRoute(
              this.currentLocale,
              newSearchModel.withPage(1),
              this.$route,
              this.$store,
            ),
          )
          .catch(() => true);

        trackSearchSubmit(newSearchModel, GTMPageType.RESULT_LIST_PAGE, 'changeSearch');
      } else if (shouldRetryListSearch) {
        this.doSearch();
        /* eslint-disable-next-line sonarjs/no-duplicate-string */
        trackSearchSubmit(newSearchModel, GTMPageType.RESULT_LIST_PAGE, 'changeSearch');
      }
    },
    async doSearch() {
      await this.$store.dispatch('resultList/doSearch', {
        name: 'fullSearch',
        searchModel: this.searchModel,
        language: this.currentLocale,
        fieldset: Fieldset.WEB_SRP_LIST,
        layoutSolutionRequest: this.layoutSolutionRequest,
        ...(this.ssrContext?.passThroughHeaders && {
          passThroughHeaders: getPassThroughHeaders(this.ssrContext.passThroughHeaders),
        }),
      });
    },
    getVisibleListingsCount(): number {
      return this.fullSearchResult?.listings.length ?? 0;
    },
    pageLink(page: number) {
      return createLayoutSolutionSRPRoute(
        this.currentLocale,
        this.searchModel.withPage(page),
        this.$route,
        this.$store,
      );
    },
    sortingChanged({ sortType, sortDirection }: ResultListHeaderSorting) {
      this.$router
        .push(
          createLayoutSolutionSRPRoute(
            this.currentLocale,
            this.searchModel.withPage(1).withSort(sortType, sortDirection),
            this.$route,
            this.$store,
          ),
        )
        .catch(() => true);
    },
    alternatePageLink(language: HgSupportedLanguage): string {
      const route = createLayoutSolutionSRPRoute(
        language,
        this.searchModel,
        this.$route,
        this.$store,
      );
      return this.$router.resolve(route).href;
    },
  },
  async serverPrefetch() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const $this = this as any;
    await $this.doSearch();

    try {
      await pWaitFor(() => $this.readyToRenderSSR, {
        timeout: { milliseconds: 16000 },
      });

      if ($this.fullSearchDataFetchError) {
        this.ssrContext.error = isPermanentError($this.fullSearchDataFetchError)
          ? 'PAGE_NOT_FOUND'
          : 'DEPENDENCY_FAILED_ERROR';
      }
    } catch (error) {
      this.ssrContext.error = 'OTHER_ERROR'; // will send HTTP 500
    }
  },
});
</script>

<style lang="scss" module>
@import '@/assets/variables';
@import '@/components/SearchBar/mixins';

$srp-blocks-spacing: $space-200;
$search-bar-padding: $space-150;

.noResults {
  margin-block: $space-300;
}

.searchBarUsabilityHelper {
  position: absolute;
  top: 2%;
  display: block;
  width: 100%;
  height: 100vh;
  background: #0000;
}

.paginationHolder {
  min-width: calc(100% + $space-200);
  max-width: calc(100% + $space-200);
  margin: $space-150 (-$space-100);
}

.visibleSearchModal.searchBarWrapper {
  z-index: 999;
}

.listingCardPlaceholder {
  padding-top: $space-100;
  padding-bottom: $space-100;
}

// Only apply height: auto when inside an iframe to prevent stretching issues
:global(.is-iframe) [data-test='searchBarModal'] {
  top: $space-300;
  height: auto;
}

.searchBarContainer {
  display: flex;
  gap: $space-100;
  justify-content: space-between;

  .searchBar {
    flex: 1;
  }

  .resultsSortBySelect {
    flex: 1;
  }
}

@media (min-width: $viewport-md) {
  .resultsSortBySelect {
    display: none;
  }
}
</style>
